import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useLanguage } from '../lib/language';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import Sidebar from '../components/Sidebar';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Camera,
  Images,
  Eye,
  Download,
  Plus,
  ChevronRight,
  Loader2,
  CreditCard,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay }}
  >
    <Card className="bg-card border-border hover:border-primary/30 transition-colors">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground mb-1">{label}</p>
            <p className="text-3xl font-bold">{value}</p>
          </div>
          <div className="w-12 h-12 bg-secondary rounded-sm flex items-center justify-center">
            <Icon className="w-6 h-6 text-muted-foreground" />
          </div>
        </div>
      </CardContent>
    </Card>
  </motion.div>
);

const GalleryCard = ({ gallery, delay }) => {
  const navigate = useNavigate();
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay }}
      onClick={() => navigate(`/galleries/${gallery.id}`)}
      className="cursor-pointer"
    >
      <Card className="bg-card border-border hover:border-primary/30 transition-all hover:-translate-y-1">
        <CardContent className="p-5">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="font-semibold truncate">{gallery.name}</h3>
              <p className="text-sm text-muted-foreground">{gallery.client_name}</p>
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </div>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Images className="w-4 h-4" />
              {gallery.photo_count}
            </span>
            <span className="flex items-center gap-1">
              <Eye className="w-4 h-4" />
              {gallery.views}
            </span>
            <span className="flex items-center gap-1">
              <Download className="w-4 h-4" />
              {gallery.downloads}
            </span>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default function DashboardPage() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [billingAccount, setBillingAccount] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
    fetchBillingAccount();
  }, []);

  const fetchBillingAccount = async () => {
    try {
      const res = await axios.get(`${API}/billing/account`);
      setBillingAccount(res.data);
    } catch { /* ignore */ }
  };

  const fetchDashboardStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar activePath="/dashboard" />

      {/* Main Content */}
      <main className="lg:ml-64 p-6 lg:p-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1 className="text-3xl font-bold mb-1">
              {t('dashboard.welcome')} {user?.name?.split(' ')[0]}
            </h1>
            <p className="text-muted-foreground">
              {t('settings.currentPlan')}: <span className="text-foreground font-medium capitalize">{user?.plan || 'Free'}</span>
            </p>
          </div>
          <Button onClick={() => navigate('/galleries/new')} data-testid="create-gallery-btn">
            <Plus className="w-4 h-4 mr-2" />
            {t('dashboard.createGallery')}
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          <StatCard 
            icon={Images} 
            label={t('dashboard.stats.galleries')} 
            value={stats?.total_galleries || 0} 
            delay={0}
          />
          <StatCard 
            icon={Camera} 
            label={t('dashboard.stats.photos')} 
            value={stats?.total_photos || 0} 
            delay={0.1}
          />
          <StatCard 
            icon={Eye} 
            label={t('dashboard.stats.views')} 
            value={stats?.total_views || 0} 
            delay={0.2}
          />
          <StatCard 
            icon={Download} 
            label={t('dashboard.stats.downloads')} 
            value={stats?.total_downloads || 0} 
            delay={0.3}
          />
        </div>

        {/* Billing Credits */}
        {billingAccount && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.4 }}
            className="mb-10"
          >
            <Card
              className="bg-card border-border hover:border-primary/30 transition-colors cursor-pointer"
              onClick={() => navigate('/billing')}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-secondary rounded-sm flex items-center justify-center">
                      <CreditCard className="w-6 h-6 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">
                        {language === 'pl' ? 'Kredyty galerii' : 'Gallery credits'}
                      </p>
                      {billingAccount.annual_unlimited ? (
                        <p className="text-lg font-bold">{language === 'pl' ? 'Plan Unlimited' : 'Unlimited Plan'}</p>
                      ) : (
                        <p className="text-lg font-bold">
                          {billingAccount.credits_s || 0}×S &nbsp;
                          {billingAccount.credits_m || 0}×M &nbsp;
                          {billingAccount.credits_l || 0}×L
                        </p>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Recent Galleries */}
        <div className="mb-6 flex items-center justify-between">
          <h2 className="text-xl font-semibold">{t('dashboard.recentGalleries')}</h2>
          <Link to="/galleries" className="text-sm text-muted-foreground hover:text-foreground">
            {t('dashboard.viewAll')} →
          </Link>
        </div>

        {stats?.recent_galleries?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {stats.recent_galleries.map((gallery, idx) => (
              <GalleryCard key={gallery.id} gallery={gallery} delay={0.1 * idx} />
            ))}
          </div>
        ) : (
          <Card className="bg-card border-border">
            <CardContent className="p-12 text-center">
              <Images className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-2">{t('dashboard.noGalleries')}</h3>
              <p className="text-muted-foreground mb-6">
                {language === 'pl' ? 'Utwórz swoją pierwszą galerię' : 'Create your first gallery'}
              </p>
              <Button onClick={() => navigate('/galleries/new')} data-testid="empty-create-gallery-btn">
                <Plus className="w-4 h-4 mr-2" />
                {t('dashboard.createGallery')}
              </Button>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
